import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from "@hangar/web-components/core/Button";
import { Text } from "@hangar/web-components/core/Text";
import { useTranslation } from '../../hooks/i18n/useTranslation';
import * as S from './styles/ActionConfirmationModal.style';

export type ActionConfirmationModalVariant = 'warning' | 'confirmation';

export interface ActionConfirmationModalProps {
  isOpen: boolean;
  variant?: ActionConfirmationModalVariant;
  title: string;
  message: string | React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const CloseIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const ActionConfirmationModal: React.FC<ActionConfirmationModalProps> = ({
  isOpen,
  variant = 'confirmation',
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const isWarning = variant === 'warning';

  const handleConfirmClick = useCallback(() => {
    if (isSubmitting) return;
    onConfirm();
  }, [isSubmitting, onConfirm]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (isSubmitting) return;
      onCancel();
      return;
    }

    if (e.key === 'Tab' && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, [onCancel, isSubmitting]);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      document.addEventListener('keydown', handleKeyDown);
      
      const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();

      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (isSubmitting) return;
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const modalId = `action-confirmation-modal-${variant}`;

  return createPortal(
    <S.Overlay 
      onClick={handleOverlayClick}
      role="presentation"
    >
      <S.ModalContainer
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${modalId}-title`}
        aria-describedby={`${modalId}-description`}
      >
        <S.ModalHeader>
          <S.HeaderContent>
            <Text variant="heading-md" bold={true} color="tertiary" id={`${modalId}-title`}>
              {title}
            </Text>
          </S.HeaderContent>
          <S.CloseButton
            type="button"
            disabled={isSubmitting}
            onClick={onCancel}
            aria-label={t('common.close')}
          >
            <CloseIcon />
          </S.CloseButton>
        </S.ModalHeader>
        
        <S.ModalBody $hasIcon={isWarning} id={`${modalId}-description`}>
          {typeof message === 'string' ? (
            <Text variant="body-md">{message}</Text>
          ) : (
            message
          )}
        </S.ModalBody>
        
        <S.ModalFooter>
          <Button
            variant="secondary"
            label={cancelLabel}
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <Button
            variant="primary"
            loading={isSubmitting}
            aria-busy={isSubmitting}
            aria-disabled={isSubmitting}
            label={isSubmitting ? t('actionConfirmation.processing') : confirmLabel}
            onClick={handleConfirmClick}
          />
        </S.ModalFooter>
      </S.ModalContainer>
    </S.Overlay>,
    document.body
  );
};

export { HighlightText } from './styles/ActionConfirmationModal.style';
